import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import { useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import classNames from 'classnames';

import {
  AppBar, Toolbar, Typography, withStyles
} from '@material-ui/core';
import { I18n, Translate } from 'src/translations/i18n';

import dayjs from 'dayjs';

import { setAppLang } from 'src/store/actions/translationActions';
import { setGlobalPeriod } from 'src/store/actions/personalConfigActions';

import { selectGlobalPeriod, selectShowGlobalPeriod } from 'src/store/selectors/personalConfiguration';

import { selectUserInfo, selectVersions } from 'src/store/selectors/userInfo';
import { selectLocale } from 'src/store/selectors/translationSelectors';

import HelpHeaderBar from 'src/containers/Help/Components/HelpHeaderBar';
import Utils from 'src/utils';
import { useAuth } from 'react-oidc-context';
import useThemeSwitch from 'src/style/themes/useThemeSwitch';
import Inspectable from 'src/components/EntityInspector/Inspectable/Inspectable';
import { EntityEndpoint } from 'src/interfaces/modular/entityInfoTypes';
import { getEntity } from 'src/components/EntityInspector/EntityEntryPointHelper';
import { DO_NOTHING } from 'src/helpers/FPUtils';
import DateRangePicker from '../Inputs/DateRangePicker';
import UserCabinet from '../UserCabinet';

const drawerWidth = 240;

const styles = (theme) => ({
  appBar: {
    height: 64,
    margin: 0,
    top: 0,
    backgroundColor: theme.palette.header,
    transition: theme.transitions.create('all', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    }),
    '@media print': {
      visibility: 'hidden'
    }
  },
  appBarShift: {
    margin: `0, 0, 0, ${drawerWidth}px`,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create('all', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    })
  },
  pageHeader: {
    alignItems: 'center',
    marginLeft: theme.spacing(9),
    color: 'white',
    display: 'flex',
    transition: theme.transitions.create('all', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    })
  },
  pageHeaderOpen: {
    marginLeft: 0,
    transition: theme.transitions.create('all', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    })
  },
  globalPeriodContainer: {
    paddingLeft: 36
  },
  periodSelectorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    '& label': {
      color: '#FFF',
      fontWeight: '100'
    }
  },
  periodSelectorButton: {
    color: '#FFF',
    borderColor: '#FFF',
    marginLeft: 18
  },
  companyLabel: {
    margin: 0,
    padding: 8,
    opacity: 0.7,
    fontSize: '16px',
    fontWeight: 400
  }
});

function Header({
  classes, drawerOpen, selectedNav
}) {
  const dispatch = useDispatch();
  const location = useLocation();
  const auth = useAuth();
  const userInfo = useSelector(selectUserInfo);
  const versions = useSelector(selectVersions);
  const selectedLang = useSelector(selectLocale);
  const globalPeriod = useSelector(selectGlobalPeriod);
  const showGlobalPeriod = useSelector(selectShowGlobalPeriod);

  const [themeType, changeTheme] = useThemeSwitch();

  const handleChangeOfLang = (language) => {
    dispatch(setAppLang(language));
  };

  const handleChangeGlobalPeriod = (val) => {
    dispatch(setGlobalPeriod({
      startDate: dayjs(val.startDate).format('DD-MM-YYYY'),
      endDate: dayjs(val.endDate).format('DD-MM-YYYY')
    }));
  };

  const globalPeriodValue = useMemo(
    () => ({
      startDate: dayjs(globalPeriod.startDate, 'DD-MM-YYYY'),
      endDate: dayjs(globalPeriod.endDate, 'DD-MM-YYYY')
    }),
    [globalPeriod]
  );

  return (
    <AppBar
      position="absolute"
      className={classNames(classes.appBar, drawerOpen && classes.appBarShift)}
    >
      <Toolbar style={{ justifyContent: 'space-between' }}>
        <>
          <div className={classNames(classes.pageHeader, drawerOpen && classes.pageHeaderOpen)}>
            <Typography variant="h5" style={{ color: 'white' }} className="line-clamp-ellipsis">
              <Translate value={selectedNav && selectedNav.selectedTabId ? selectedNav.selectedTabId : ''} />
            </Typography>
          </div>
          {showGlobalPeriod && (
            <div className={classes.globalPeriodContainer}>
              <DateRangePicker
                value={globalPeriodValue}
                onChange={handleChangeGlobalPeriod}
                periods={Utils.periods.defaultPeriods}
                singleDatePicker={false}
                showTimePickers={false}
                timeOptions={false}
                label="GlobalPeriod"
                containerClassName={classes.periodSelectorContainer}
                buttonClassName={classes.periodSelectorButton}
                disabledDate={() => false}
              />
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <p className={classes.companyLabel}>
              <Inspectable endpoint={EntityEndpoint.Central} entity={getEntity.company(userInfo.CompanyId)}>
                {userInfo.CompanyName}
              </Inspectable>
            </p>

            <HelpHeaderBar pathname={location.pathname} />
            <UserCabinet
              changeTheme={changeTheme}
              themeType={themeType}
              user={auth.user?.profile?.name || ''}
              userInfo={userInfo}
              versions={versions}
              handleChangeOfLang={handleChangeOfLang}
              selectedLang={selectedLang}
            />
          </div>
        </>
      </Toolbar>
    </AppBar>
  );
}

Header.propTypes = {
  classes: PropTypes.object.isRequired,
  drawerOpen: PropTypes.bool.isRequired,
  selectedNav: PropTypes.object.isRequired
};

export default withStyles(styles)(Header);
